import type {
  ColorByConfig,
  CrossPlotChartConfig,
  FilterCategory,
} from '../types';
// ─── Cross Plot Charts (multi-chart configuration) ───
//
// Users can configure any number of cross-plot charts on the Type Curve page.
// Each chart owns its own title + X/Y axis selection + visibility flag and
// drives an independent API call in TypeCurveCrossPlot. A single default chart
// ("Well Stimulations") is seeded so the page is never empty on first load.

/** Selectable cross-plot axis. Mirrors `AXIS_CONFIG` in `app/models/query/cross_plot.rb`. */
export type CrossPlotAxis =
  | 'acidIntensityBbl'
  | 'acidIntensityGal'
  | 'cgr'
  | 'fluidIntensityBbl'
  | 'fluidIntensityGal'
  | 'gasEurMcf'
  | 'gasEurPerFt'
  | 'gor'
  | 'oilEurBbl'
  | 'oilEurPerFt'
  | 'perfLateralLength'
  | 'proppantIntensity'
  | 'scaledGasEur'
  | 'scaledOilEur'
  | 'scaledWaterEur'
  | 'stageSpacing'
  | 'trueVerticalDepth'
  | 'waterEurBbl'
  | 'waterEurPerFt'
  | 'waterIntensityBbl'
  | 'waterIntensityGal'
  | 'watercut'
  | 'wgr'
  | 'wor'
  // Well Spacing — any zone (top-level)
  | 'numberOfParents'
  | 'numberOfChildren'
  | 'numberOfSiblings'
  | 'closestAnyZoneTrueDistanceFt'
  | 'closestAnyZoneHzDistanceFt'
  | 'closestAnyZoneVtDistanceFt'
  | 'closestAnyZoneOverlapPct'
  | 'secondClosestAnyZoneTrueDistanceFt'
  | 'secondClosestAnyZoneHzDistanceFt'
  | 'secondClosestAnyZoneVtDistanceFt'
  | 'secondClosestAnyZoneOverlapPct'
  | 'avgAnyZoneTrueDistanceFt'
  | 'avgAnyZoneHzDistanceFt'
  | 'avgAnyZoneVtDistanceFt'
  // Landing + in-zone spacing (requires structure_grid_collection_id)
  | 'distanceToTopFt'
  | 'azimuthDeg'
  | 'dipDeg'
  | 'closestInZoneTrueDistanceFt'
  | 'closestInZoneHzDistanceFt'
  | 'closestInZoneVtDistanceFt'
  | 'closestInZoneOverlapPct'
  | 'secondClosestInZoneTrueDistanceFt'
  | 'secondClosestInZoneHzDistanceFt'
  | 'secondClosestInZoneVtDistanceFt'
  | 'secondClosestInZoneOverlapPct'
  | 'avgInZoneTrueDistanceFt'
  | 'avgInZoneHzDistanceFt'
  | 'avgInZoneVtDistanceFt'
  // Dates
  | 'permitDate'
  | 'spudDate'
  | 'completionDate'
  | 'firstProductionDate'
  | 'jobDate';

/** Display label for each axis. Sourced from `AXIS_CONFIG` (`app/models/query/cross_plot.rb`). */
export const CROSS_PLOT_AXIS_LABELS: Record<CrossPlotAxis, string> = {
  acidIntensityBbl: 'Acid Intensity (bbl/ft)',
  acidIntensityGal: 'Acid Intensity (gal/ft)',
  cgr: 'CGR (bbl/Mmcf)',
  fluidIntensityBbl: 'Fluid Intensity (bbl/ft)',
  fluidIntensityGal: 'Fluid Intensity (gal/ft)',
  gasEurMcf: 'Gas EUR (mcf)',
  gasEurPerFt: 'Gas EUR (mcf/ft)',
  gor: 'GOR (mcf/bbl)',
  oilEurBbl: 'Oil EUR (bbl)',
  oilEurPerFt: 'Oil EUR (bbl/ft)',
  perfLateralLength: 'Lateral Length (ft)',
  proppantIntensity: 'Proppant Intensity (lbs/ft)',
  scaledGasEur: 'Scaled Gas EUR (mcf)',
  scaledOilEur: 'Scaled Oil EUR (bbl)',
  scaledWaterEur: 'Scaled Water EUR (bbl)',
  stageSpacing: 'Stage Spacing (ft)',
  trueVerticalDepth: 'True Vertical Depth (ft)',
  waterEurBbl: 'Water EUR (bbl)',
  waterEurPerFt: 'Water EUR (bbl/ft)',
  waterIntensityBbl: 'Water Intensity (bbl/ft)',
  waterIntensityGal: 'Water Intensity (gal/ft)',
  watercut: 'Watercut (%)',
  wgr: 'WGR (bbl/Mmcf)',
  wor: 'WOR (bbl/bbl)',
  // Well Spacing — any zone
  numberOfParents: 'Number of Parents',
  numberOfChildren: 'Number of Children',
  numberOfSiblings: 'Number of Siblings',
  closestAnyZoneTrueDistanceFt: 'Closest Any-Zone True Distance (ft)',
  closestAnyZoneHzDistanceFt: 'Closest Any-Zone Horizontal Distance (ft)',
  closestAnyZoneVtDistanceFt: 'Closest Any-Zone Vertical Distance (ft)',
  closestAnyZoneOverlapPct: 'Closest Any-Zone Overlap (%)',
  secondClosestAnyZoneTrueDistanceFt: '2nd Closest Any-Zone True Distance (ft)',
  secondClosestAnyZoneHzDistanceFt:
    '2nd Closest Any-Zone Horizontal Distance (ft)',
  secondClosestAnyZoneVtDistanceFt:
    '2nd Closest Any-Zone Vertical Distance (ft)',
  secondClosestAnyZoneOverlapPct: '2nd Closest Any-Zone Overlap (%)',
  avgAnyZoneTrueDistanceFt: 'Avg Any-Zone True Distance (ft)',
  avgAnyZoneHzDistanceFt: 'Avg Any-Zone Horizontal Distance (ft)',
  avgAnyZoneVtDistanceFt: 'Avg Any-Zone Vertical Distance (ft)',
  // Landing + in-zone spacing
  distanceToTopFt: 'Distance to Zone Top (ft)',
  azimuthDeg: 'Azimuth (deg)',
  dipDeg: 'Dip (deg)',
  closestInZoneTrueDistanceFt: 'Closest In-Zone True Distance (ft)',
  closestInZoneHzDistanceFt: 'Closest In-Zone Horizontal Distance (ft)',
  closestInZoneVtDistanceFt: 'Closest In-Zone Vertical Distance (ft)',
  closestInZoneOverlapPct: 'Closest In-Zone Overlap (%)',
  secondClosestInZoneTrueDistanceFt: '2nd Closest In-Zone True Distance (ft)',
  secondClosestInZoneHzDistanceFt:
    '2nd Closest In-Zone Horizontal Distance (ft)',
  secondClosestInZoneVtDistanceFt: '2nd Closest In-Zone Vertical Distance (ft)',
  secondClosestInZoneOverlapPct: '2nd Closest In-Zone Overlap (%)',
  avgInZoneTrueDistanceFt: 'Avg In-Zone True Distance (ft)',
  avgInZoneHzDistanceFt: 'Avg In-Zone Horizontal Distance (ft)',
  avgInZoneVtDistanceFt: 'Avg In-Zone Vertical Distance (ft)',
  // Dates
  permitDate: 'Permit Date',
  spudDate: 'Spud Date',
  completionDate: 'Completion Date',
  firstProductionDate: 'First Production Date',
  jobDate: 'Job Date',
};

/** The date axes, in well-lifecycle order — which is the order the dropdown shows them in. */
export const CROSS_PLOT_DATE_AXES: CrossPlotAxis[] = [
  'permitDate',
  'spudDate',
  'completionDate',
  'firstProductionDate',
  'jobDate',
];

const CROSS_PLOT_DATE_AXIS_SET = new Set<CrossPlotAxis>(CROSS_PLOT_DATE_AXES);

export const isCrossPlotDateAxis = (axis: CrossPlotAxis): boolean =>
  CROSS_PLOT_DATE_AXIS_SET.has(axis);

/**
 * Ordered list of axis options used to populate the X / Y axis dropdowns:
 * the measures alphabetically, then the dates.
 */
export const CROSS_PLOT_AXIS_OPTIONS: {
  id: CrossPlotAxis;
  label: string;
}[] = [
  ...(Object.keys(CROSS_PLOT_AXIS_LABELS) as CrossPlotAxis[])
    .filter((id) => !isCrossPlotDateAxis(id))
    .map((id) => ({ id, label: CROSS_PLOT_AXIS_LABELS[id] }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  ...CROSS_PLOT_DATE_AXES.map((id) => ({
    id,
    label: CROSS_PLOT_AXIS_LABELS[id],
  })),
];

/** Stable id for the default "Well Stimulations" chart so callers can detect it. */
export const DEFAULT_CROSS_PLOT_CHART_ID =
  'cross-plot-default-well-stimulations';

export const DEFAULT_CROSS_PLOT_CHART: CrossPlotChartConfig = {
  id: DEFAULT_CROSS_PLOT_CHART_ID,
  title: 'Well Stimulations',
  xAxis: 'proppantIntensity',
  yAxis: 'fluidIntensityBbl',
  visible: true,
};

export const TC_WELL_FILTER_CATEGORIES: FilterCategory[] = [
  // ── Identity ──
  {
    id: 'uwi',
    label: 'UWI',
    type: 'picklist',
    param: 'uwi',
    facetKey: 'uwi',
    accessor: (w) => w.uwi,
  },
  {
    id: 'wellName',
    label: 'Well Name',
    type: 'picklist',
    param: 'wellName',
    facetKey: 'wellName',
    accessor: (w) => w.name,
  },
  {
    id: 'leaseName',
    label: 'Lease Name',
    type: 'picklist',
    param: 'leaseName',
    facetKey: 'leaseName',
    accessor: (w) => w.leaseName,
  },
  {
    id: 'wellNumber',
    label: 'Well Number',
    type: 'picklist',
    param: 'wellNumber',
    facetKey: 'wellNumber',
    accessor: (w) => w.wellNumber,
  },
  {
    id: 'currentOperator',
    label: 'Current Operator',
    type: 'picklist',
    param: 'currentOperator',
    facetKey: 'currentOperator',
    accessor: (w) => w.currentOperator,
  },
  {
    id: 'state',
    label: 'State',
    type: 'picklist',
    param: 'state',
    facetKey: 'state',
    accessor: (w) => w.stateAbbreviation,
  },
  {
    id: 'status',
    label: 'Status',
    type: 'picklist',
    param: 'status',
    facetKey: 'status',
    accessor: (w) => w.status,
  },
  {
    id: 'holeDirection',
    label: 'Hole Direction',
    type: 'picklist',
    param: 'holeDirection',
    facetKey: 'holeDirection',
    accessor: (w) => w.holeDirection,
  },

  // ── Geology / Landing Analysis ──
  {
    id: 'landingZone',
    label: 'Landing Zone',
    type: 'picklist',
    param: 'landingZone',
    facetKey: 'landingZone',
    accessor: (w) => w.landingZone,
  },
  {
    id: 'formation',
    label: 'Formation',
    type: 'picklist',
    param: 'formation',
    facetKey: 'formation',
    accessor: (w) => w.formation,
  },
  {
    id: 'distanceToTopFt',
    label: 'Distance to Top (ft)',
    type: 'numeric-range',
    param: 'distanceToTopFt',
    facetKey: 'distanceToTopFt',
    accessor: (w) => w.distanceToTopFt,
    minParam: 'min_distanceToTopFt',
    maxParam: 'max_distanceToTopFt',
  },
  // ── Well Spacing ──
  {
    id: 'parentChildStatus',
    label: 'Parent/Child Status',
    type: 'picklist',
    param: 'parentChildStatus',
    facetKey: 'parentChildStatus',
    accessor: (w) => w.parentChildStatus,
  },
  {
    id: 'closestInZoneTrueDistanceFt',
    label: 'Closest Neighbor In Zone True Dist (ft)',
    type: 'numeric-range',
    param: 'closestInZoneTrueDistanceFt',
    facetKey: 'closestInZoneTrueDistanceFt',
    accessor: (w) => w.closestInZoneTrueDistanceFt,
    minParam: 'min_closestInZoneTrueDistanceFt',
    maxParam: 'max_closestInZoneTrueDistanceFt',
  },
  {
    id: 'closestInZoneHzDistanceFt',
    label: 'Closest Neighbor In Zone Hz Dist (ft)',
    type: 'numeric-range',
    param: 'closestInZoneHzDistanceFt',
    facetKey: 'closestInZoneHzDistanceFt',
    accessor: (w) => w.closestInZoneHzDistanceFt,
    minParam: 'min_closestInZoneHzDistanceFt',
    maxParam: 'max_closestInZoneHzDistanceFt',
  },
  {
    id: 'closestInZoneVtDistanceFt',
    label: 'Closest Neighbor In Zone Vt Dist (ft)',
    type: 'numeric-range',
    param: 'closestInZoneVtDistanceFt',
    facetKey: 'closestInZoneVtDistanceFt',
    accessor: (w) => w.closestInZoneVtDistanceFt,
    minParam: 'min_closestInZoneVtDistanceFt',
    maxParam: 'max_closestInZoneVtDistanceFt',
  },
  {
    id: 'closestInZoneOverlapPct',
    label: 'Closest Neighbor In Zone Overlap (%)',
    type: 'numeric-range',
    param: 'closestInZoneOverlapPct',
    facetKey: 'closestInZoneOverlapPct',
    accessor: (w) => w.closestInZoneOverlapPct,
    minParam: 'min_closestInZoneOverlapPct',
    maxParam: 'max_closestInZoneOverlapPct',
  },
  {
    id: 'secondClosestInZoneTrueDistanceFt',
    label: '2nd Closest Neighbor In Zone True Dist (ft)',
    type: 'numeric-range',
    param: 'secondClosestInZoneTrueDistanceFt',
    facetKey: 'secondClosestInZoneTrueDistanceFt',
    accessor: (w) => w.secondClosestInZoneTrueDistanceFt,
    minParam: 'min_secondClosestInZoneTrueDistanceFt',
    maxParam: 'max_secondClosestInZoneTrueDistanceFt',
  },
  {
    id: 'secondClosestInZoneHzDistanceFt',
    label: '2nd Closest Neighbor In Zone Hz Dist (ft)',
    type: 'numeric-range',
    param: 'secondClosestInZoneHzDistanceFt',
    facetKey: 'secondClosestInZoneHzDistanceFt',
    accessor: (w) => w.secondClosestInZoneHzDistanceFt,
    minParam: 'min_secondClosestInZoneHzDistanceFt',
    maxParam: 'max_secondClosestInZoneHzDistanceFt',
  },
  {
    id: 'secondClosestInZoneVtDistanceFt',
    label: '2nd Closest Neighbor In Zone Vt Dist (ft)',
    type: 'numeric-range',
    param: 'secondClosestInZoneVtDistanceFt',
    facetKey: 'secondClosestInZoneVtDistanceFt',
    accessor: (w) => w.secondClosestInZoneVtDistanceFt,
    minParam: 'min_secondClosestInZoneVtDistanceFt',
    maxParam: 'max_secondClosestInZoneVtDistanceFt',
  },
  {
    id: 'secondClosestInZoneOverlapPct',
    label: '2nd Closest Neighbor In Zone Overlap (%)',
    type: 'numeric-range',
    param: 'secondClosestInZoneOverlapPct',
    facetKey: 'secondClosestInZoneOverlapPct',
    accessor: (w) => w.secondClosestInZoneOverlapPct,
    minParam: 'min_secondClosestInZoneOverlapPct',
    maxParam: 'max_secondClosestInZoneOverlapPct',
  },
  {
    id: 'avgInZoneTrueDistanceFt',
    label: 'Avg Spacing In Zone True Dist (ft)',
    type: 'numeric-range',
    param: 'avgInZoneTrueDistanceFt',
    facetKey: 'avgInZoneTrueDistanceFt',
    accessor: (w) => w.avgInZoneTrueDistanceFt,
    minParam: 'min_avgInZoneTrueDistanceFt',
    maxParam: 'max_avgInZoneTrueDistanceFt',
  },
  {
    id: 'avgInZoneHzDistanceFt',
    label: 'Avg Spacing In Zone Hz Dist (ft)',
    type: 'numeric-range',
    param: 'avgInZoneHzDistanceFt',
    facetKey: 'avgInZoneHzDistanceFt',
    accessor: (w) => w.avgInZoneHzDistanceFt,
    minParam: 'min_avgInZoneHzDistanceFt',
    maxParam: 'max_avgInZoneHzDistanceFt',
  },
  {
    id: 'avgInZoneVtDistanceFt',
    label: 'Avg Spacing In Zone Vt Dist (ft)',
    type: 'numeric-range',
    param: 'avgInZoneVtDistanceFt',
    facetKey: 'avgInZoneVtDistanceFt',
    accessor: (w) => w.avgInZoneVtDistanceFt,
    minParam: 'min_avgInZoneVtDistanceFt',
    maxParam: 'max_avgInZoneVtDistanceFt',
  },
  {
    id: 'closestAnyZoneTrueDistanceFt',
    label: 'Closest Neighbor Any Zone True Dist (ft)',
    type: 'numeric-range',
    param: 'closestAnyZoneTrueDistanceFt',
    facetKey: 'closestAnyZoneTrueDistanceFt',
    accessor: (w) => w.closestAnyZoneTrueDistanceFt,
    minParam: 'min_closestAnyZoneTrueDistanceFt',
    maxParam: 'max_closestAnyZoneTrueDistanceFt',
  },
  {
    id: 'closestAnyZoneHzDistanceFt',
    label: 'Closest Neighbor Any Zone Hz Dist (ft)',
    type: 'numeric-range',
    param: 'closestAnyZoneHzDistanceFt',
    facetKey: 'closestAnyZoneHzDistanceFt',
    accessor: (w) => w.closestAnyZoneHzDistanceFt,
    minParam: 'min_closestAnyZoneHzDistanceFt',
    maxParam: 'max_closestAnyZoneHzDistanceFt',
  },
  {
    id: 'closestAnyZoneVtDistanceFt',
    label: 'Closest Neighbor Any Zone Vt Dist (ft)',
    type: 'numeric-range',
    param: 'closestAnyZoneVtDistanceFt',
    facetKey: 'closestAnyZoneVtDistanceFt',
    accessor: (w) => w.closestAnyZoneVtDistanceFt,
    minParam: 'min_closestAnyZoneVtDistanceFt',
    maxParam: 'max_closestAnyZoneVtDistanceFt',
  },
  {
    id: 'closestAnyZoneOverlapPct',
    label: 'Closest Neighbor Any Zone Overlap (%)',
    type: 'numeric-range',
    param: 'closestAnyZoneOverlapPct',
    facetKey: 'closestAnyZoneOverlapPct',
    accessor: (w) => w.closestAnyZoneOverlapPct,
    minParam: 'min_closestAnyZoneOverlapPct',
    maxParam: 'max_closestAnyZoneOverlapPct',
  },
  {
    id: 'secondClosestAnyZoneTrueDistanceFt',
    label: '2nd Closest Neighbor Any Zone True Dist (ft)',
    type: 'numeric-range',
    param: 'secondClosestAnyZoneTrueDistanceFt',
    facetKey: 'secondClosestAnyZoneTrueDistanceFt',
    accessor: (w) => w.secondClosestAnyZoneTrueDistanceFt,
    minParam: 'min_secondClosestAnyZoneTrueDistanceFt',
    maxParam: 'max_secondClosestAnyZoneTrueDistanceFt',
  },
  {
    id: 'secondClosestAnyZoneHzDistanceFt',
    label: '2nd Closest Neighbor Any Zone Hz Dist (ft)',
    type: 'numeric-range',
    param: 'secondClosestAnyZoneHzDistanceFt',
    facetKey: 'secondClosestAnyZoneHzDistanceFt',
    accessor: (w) => w.secondClosestAnyZoneHzDistanceFt,
    minParam: 'min_secondClosestAnyZoneHzDistanceFt',
    maxParam: 'max_secondClosestAnyZoneHzDistanceFt',
  },
  {
    id: 'secondClosestAnyZoneVtDistanceFt',
    label: '2nd Closest Neighbor Any Zone Vt Dist (ft)',
    type: 'numeric-range',
    param: 'secondClosestAnyZoneVtDistanceFt',
    facetKey: 'secondClosestAnyZoneVtDistanceFt',
    accessor: (w) => w.secondClosestAnyZoneVtDistanceFt,
    minParam: 'min_secondClosestAnyZoneVtDistanceFt',
    maxParam: 'max_secondClosestAnyZoneVtDistanceFt',
  },
  {
    id: 'secondClosestAnyZoneOverlapPct',
    label: '2nd Closest Neighbor Any Zone Overlap (%)',
    type: 'numeric-range',
    param: 'secondClosestAnyZoneOverlapPct',
    facetKey: 'secondClosestAnyZoneOverlapPct',
    accessor: (w) => w.secondClosestAnyZoneOverlapPct,
    minParam: 'min_secondClosestAnyZoneOverlapPct',
    maxParam: 'max_secondClosestAnyZoneOverlapPct',
  },
  {
    id: 'avgAnyZoneTrueDistanceFt',
    label: 'Avg Spacing Any Zone True Dist (ft)',
    type: 'numeric-range',
    param: 'avgAnyZoneTrueDistanceFt',
    facetKey: 'avgAnyZoneTrueDistanceFt',
    accessor: (w) => w.avgAnyZoneTrueDistanceFt,
    minParam: 'min_avgAnyZoneTrueDistanceFt',
    maxParam: 'max_avgAnyZoneTrueDistanceFt',
  },
  {
    id: 'avgAnyZoneHzDistanceFt',
    label: 'Avg Spacing Any Zone Hz Dist (ft)',
    type: 'numeric-range',
    param: 'avgAnyZoneHzDistanceFt',
    facetKey: 'avgAnyZoneHzDistanceFt',
    accessor: (w) => w.avgAnyZoneHzDistanceFt,
    minParam: 'min_avgAnyZoneHzDistanceFt',
    maxParam: 'max_avgAnyZoneHzDistanceFt',
  },
  {
    id: 'avgAnyZoneVtDistanceFt',
    label: 'Avg Spacing Any Zone Vt Dist (ft)',
    type: 'numeric-range',
    param: 'avgAnyZoneVtDistanceFt',
    facetKey: 'avgAnyZoneVtDistanceFt',
    accessor: (w) => w.avgAnyZoneVtDistanceFt,
    minParam: 'min_avgAnyZoneVtDistanceFt',
    maxParam: 'max_avgAnyZoneVtDistanceFt',
  },
  {
    id: 'numberOfParents',
    label: 'Number of Parents',
    type: 'numeric-range',
    param: 'numberOfParents',
    facetKey: 'numberOfParents',
    accessor: (w) => w.numberOfParents,
    minParam: 'min_numberOfParents',
    maxParam: 'max_numberOfParents',
  },
  {
    id: 'numberOfChildren',
    label: 'Number of Children',
    type: 'numeric-range',
    param: 'numberOfChildren',
    facetKey: 'numberOfChildren',
    accessor: (w) => w.numberOfChildren,
    minParam: 'min_numberOfChildren',
    maxParam: 'max_numberOfChildren',
  },
  {
    id: 'numberOfSiblings',
    label: 'Number of Siblings',
    type: 'numeric-range',
    param: 'numberOfSiblings',
    facetKey: 'numberOfSiblings',
    accessor: (w) => w.numberOfSiblings,
    minParam: 'min_numberOfSiblings',
    maxParam: 'max_numberOfSiblings',
  },
  {
    id: 'trueVerticalDepthFeet',
    label: 'True Vertical Depth (ft)',
    type: 'numeric-range',
    param: 'trueVerticalDepthFeet',
    facetKey: 'trueVerticalDepthFeet',
    accessor: (w) => w.trueVerticalDepthFeet,
    minParam: 'min_trueVerticalDepthFeet',
    maxParam: 'max_trueVerticalDepthFeet',
  },
  {
    id: 'perforatedLateralLengthFt',
    label: 'Perf Lateral Length (ft)',
    type: 'numeric-range',
    param: 'perforatedLateralLengthFt',
    facetKey: 'perforatedLateralLengthFt',
    accessor: (w) => w.perforatedLateralLengthFt,
    minParam: 'min_perforatedLateralLengthFt',
    maxParam: 'max_perforatedLateralLengthFt',
  },

  // ── Dates ──
  {
    id: 'permitDate',
    label: 'Permit Date',
    type: 'date-range',
    param: 'permitDate',
    facetKey: 'permitDate',
    accessor: (w) => w.permitDate,
    minParam: 'min_permitDate',
    maxParam: 'max_permitDate',
  },
  {
    id: 'spudDate',
    label: 'Spud Date',
    type: 'date-range',
    param: 'spudDate',
    facetKey: 'spudDate',
    accessor: (w) => w.spudDate,
    minParam: 'min_spudDate',
    maxParam: 'max_spudDate',
  },
  {
    id: 'completionDate',
    label: 'Completion Date',
    type: 'date-range',
    param: 'completionDate',
    facetKey: 'completionDate',
    accessor: (w) => w.completionDate,
    minParam: 'min_completionDate',
    maxParam: 'max_completionDate',
  },
  {
    id: 'firstProductionDate',
    label: 'First Production Date',
    type: 'date-range',
    param: 'firstProductionDate',
    facetKey: 'firstProductionDate',
    accessor: (w) => w.firstProductionDate,
    minParam: 'min_firstProductionDate',
    maxParam: 'max_firstProductionDate',
  },
  {
    id: 'jobDate',
    label: 'Job Date',
    type: 'date-range',
    param: 'jobDate',
    facetKey: 'jobDate',
    accessor: (w) => w.jobDate,
    minParam: 'min_jobDate',
    maxParam: 'max_jobDate',
  },

  // ── Qualifier ──
  {
    id: 'wellQualifier',
    label: 'Well Qualifier',
    type: 'picklist',
    param: 'wellQualifier',
    facetKey: 'wellQualifier',
    accessor: (w) => w.wellQualifier,
  },

  // ── Volume Source ──
  {
    id: 'businessId',
    label: 'Volume Source',
    type: 'picklist',
    param: 'businessId',
    facetKey: 'businessId',
    accessor: (w) => w.businessId,
  },

  // ── EUR ──
  {
    id: 'eur_oil',
    label: 'Oil EUR (Mbo)',
    type: 'numeric-range',
    param: 'eur_oil',
    facetKey: 'oilEurBbl',
    accessor: (w) => w.oilEurBbl,
    minParam: 'min_eur_oil',
    maxParam: 'max_eur_oil',
  },
  {
    id: 'eur_gas',
    label: 'Gas EUR (Mmcf)',
    type: 'numeric-range',
    param: 'eur_gas',
    facetKey: 'gasEurMcf',
    accessor: (w) => w.gasEurMcf,
    minParam: 'min_eur_gas',
    maxParam: 'max_eur_gas',
  },
  {
    id: 'eur_water',
    label: 'Water EUR (Mbbl)',
    type: 'numeric-range',
    param: 'eur_water',
    facetKey: 'waterEurBbl',
    accessor: (w) => w.waterEurBbl,
    minParam: 'min_eur_water',
    maxParam: 'max_eur_water',
  },

  // ── Scaled EUR ──
  {
    id: 'scaledOilEurBbl',
    label: 'Scaled Oil EUR (Mbo)',
    type: 'numeric-range',
    param: 'scaledOilEurBbl',
    facetKey: 'scaledOilEurBbl',
    accessor: (w) => w.scaledOilEurBbl,
    minParam: 'min_scaledOilEurBbl',
    maxParam: 'max_scaledOilEurBbl',
  },
  {
    id: 'scaledGasEurMcf',
    label: 'Scaled Gas EUR (Mmcf)',
    type: 'numeric-range',
    param: 'scaledGasEurMcf',
    facetKey: 'scaledGasEurMcf',
    accessor: (w) => w.scaledGasEurMcf,
    minParam: 'min_scaledGasEurMcf',
    maxParam: 'max_scaledGasEurMcf',
  },
  {
    id: 'scaledWaterEurBbl',
    label: 'Scaled Water EUR (Mbbl)',
    type: 'numeric-range',
    param: 'scaledWaterEurBbl',
    facetKey: 'scaledWaterEurBbl',
    accessor: (w) => w.scaledWaterEurBbl,
    minParam: 'min_scaledWaterEurBbl',
    maxParam: 'max_scaledWaterEurBbl',
  },

  // ── EUR per ft ──
  {
    id: 'oilEurPerFtBblFt',
    label: 'Oil EUR/ft (bbl/ft)',
    type: 'numeric-range',
    param: 'oilEurPerFtBblFt',
    facetKey: 'oilEurPerFtBblFt',
    accessor: (w) => w.oilEurPerFtBblFt,
    minParam: 'min_oilEurPerFtBblFt',
    maxParam: 'max_oilEurPerFtBblFt',
  },
  {
    id: 'gasEurPerFtMcfFt',
    label: 'Gas EUR/ft (Mcf/ft)',
    type: 'numeric-range',
    param: 'gasEurPerFtMcfFt',
    facetKey: 'gasEurPerFtMcfFt',
    accessor: (w) => w.gasEurPerFtMcfFt,
    minParam: 'min_gasEurPerFtMcfFt',
    maxParam: 'max_gasEurPerFtMcfFt',
  },
  {
    id: 'waterEurPerFtBblFt',
    label: 'Water EUR/ft (bbl/ft)',
    type: 'numeric-range',
    param: 'waterEurPerFtBblFt',
    facetKey: 'waterEurPerFtBblFt',
    accessor: (w) => w.waterEurPerFtBblFt,
    minParam: 'min_waterEurPerFtBblFt',
    maxParam: 'max_waterEurPerFtBblFt',
  },

  // ── Ratios ──
  {
    id: 'gorMcfBbl',
    label: 'GOR (Mcf/bbl)',
    type: 'numeric-range',
    param: 'gorMcfBbl',
    facetKey: 'gorMcfBbl',
    accessor: (w) => w.gorMcfBbl,
    minParam: 'min_gorMcfBbl',
    maxParam: 'max_gorMcfBbl',
  },
  {
    id: 'worBblBbl',
    label: 'WOR (bbl/bbl)',
    type: 'numeric-range',
    param: 'worBblBbl',
    facetKey: 'worBblBbl',
    accessor: (w) => w.worBblBbl,
    minParam: 'min_worBblBbl',
    maxParam: 'max_worBblBbl',
  },
  {
    id: 'cgrBblMmcf',
    label: 'CGR (bbl/MMcf)',
    type: 'numeric-range',
    param: 'cgrBblMmcf',
    facetKey: 'cgrBblMmcf',
    accessor: (w) => w.cgrBblMmcf,
    minParam: 'min_cgrBblMmcf',
    maxParam: 'max_cgrBblMmcf',
  },
  {
    id: 'wgrBblMmcf',
    label: 'WGR (bbl/MMcf)',
    type: 'numeric-range',
    param: 'wgrBblMmcf',
    facetKey: 'wgrBblMmcf',
    accessor: (w) => w.wgrBblMmcf,
    minParam: 'min_wgrBblMmcf',
    maxParam: 'max_wgrBblMmcf',
  },
  {
    id: 'watercutPct',
    label: 'Watercut (%)',
    type: 'numeric-range',
    param: 'watercutPct',
    facetKey: 'watercutPct',
    accessor: (w) => w.watercutPct,
    minParam: 'min_watercutPct',
    maxParam: 'max_watercutPct',
  },

  // ── Stimulation ──
  {
    id: 'proppantLbsPerFt',
    label: 'Proppant (lbs/ft)',
    type: 'numeric-range',
    param: 'proppantLbsPerFt',
    facetKey: 'proppantLbsPerFt',
    accessor: (w) => w.proppantLbsPerFt,
    minParam: 'min_proppantLbsPerFt',
    maxParam: 'max_proppantLbsPerFt',
  },
  {
    id: 'fluidGalPerFt',
    label: 'Fluid (gal/ft)',
    type: 'numeric-range',
    param: 'fluidGalPerFt',
    facetKey: 'fluidGalPerFt',
    accessor: (w) => w.fluidGalPerFt,
    minParam: 'min_fluidGalPerFt',
    maxParam: 'max_fluidGalPerFt',
  },
  {
    id: 'fluidBblPerFt',
    label: 'Fluid (bbl/ft)',
    type: 'numeric-range',
    param: 'fluidBblPerFt',
    facetKey: 'fluidBblPerFt',
    accessor: (w) => w.fluidBblPerFt,
    minParam: 'min_fluidBblPerFt',
    maxParam: 'max_fluidBblPerFt',
  },
  {
    id: 'waterGalPerFt',
    label: 'Water (gal/ft)',
    type: 'numeric-range',
    param: 'waterGalPerFt',
    facetKey: 'waterGalPerFt',
    accessor: (w) => w.waterGalPerFt,
    minParam: 'min_waterGalPerFt',
    maxParam: 'max_waterGalPerFt',
  },
  {
    id: 'waterBblPerFt',
    label: 'Water (bbl/ft)',
    type: 'numeric-range',
    param: 'waterBblPerFt',
    facetKey: 'waterBblPerFt',
    accessor: (w) => w.waterBblPerFt,
    minParam: 'min_waterBblPerFt',
    maxParam: 'max_waterBblPerFt',
  },
  {
    id: 'acidGalPerFt',
    label: 'Acid (gal/ft)',
    type: 'numeric-range',
    param: 'acidGalPerFt',
    facetKey: 'acidGalPerFt',
    accessor: (w) => w.acidGalPerFt,
    minParam: 'min_acidGalPerFt',
    maxParam: 'max_acidGalPerFt',
  },
  {
    id: 'acidBblPerFt',
    label: 'Acid (bbl/ft)',
    type: 'numeric-range',
    param: 'acidBblPerFt',
    facetKey: 'acidBblPerFt',
    accessor: (w) => w.acidBblPerFt,
    minParam: 'min_acidBblPerFt',
    maxParam: 'max_acidBblPerFt',
  },
  {
    id: 'stageSpacingFt',
    label: 'Stage Spacing (ft)',
    type: 'numeric-range',
    param: 'stageSpacingFt',
    facetKey: 'stageSpacingFt',
    accessor: (w) => w.stageSpacingFt,
    minParam: 'min_stageSpacingFt',
    maxParam: 'max_stageSpacingFt',
  },
  {
    id: 'proppantMeshSize',
    label: 'Proppant Mesh Size',
    type: 'picklist',
    param: 'proppantMeshSize',
    facetKey: 'proppantMeshSize',
    accessor: (w) => w.proppantMeshSize,
  },
  {
    id: 'fluidSystem',
    label: 'Fluid System',
    type: 'picklist',
    param: 'fluidSystem',
    facetKey: 'fluidSystem',
    accessor: (w) => w.fluidSystem,
  },
];

// ─── Units (Development Areas) filter categories ───
// Powers TcUnitFilterModal. 11 controls per Story 1 spec — all picklist
// (including overlapping/removed modeled as 2-option Yes/No picklists) plus
// acreage as a numeric range.

export const TC_UNIT_FILTER_CATEGORIES: FilterCategory[] = [
  // Identity
  {
    id: 'name',
    label: 'Unit ID',
    type: 'picklist',
    param: 'names',
    facetKey: 'unitId',
    accessor: (u) => u?.name,
  },
  {
    id: 'unitName',
    label: 'Unit Name',
    type: 'picklist',
    param: 'unitNames',
    facetKey: 'unitName',
    accessor: (u) => u?.unitName ?? u?.unit_name,
  },
  // Boolean filters (modeled as 2-option picklists "true" / "false")
  {
    id: 'overlapping',
    label: 'Overlapping Units',
    type: 'picklist',
    param: 'overlapping',
    facetKey: 'overlapping',
    accessor: (u) => (u?.overlapping ? 'true' : 'false'),
  },
  {
    id: 'removed',
    label: 'Removed Unit',
    type: 'picklist',
    param: 'removed',
    facetKey: 'removed',
    accessor: (u) => (u?.removed ? 'true' : 'false'),
  },
  {
    id: 'filedWellOnly',
    label: 'Filed Well Only',
    type: 'picklist',
    param: 'filedWellOnly',
    facetKey: 'filedWellOnly',
    accessor: (u) => (u?.filedWellOnly ? 'true' : 'false'),
    singleSelect: true,
  },
  // Categorical attributes
  {
    id: 'unitType',
    label: 'Unit Type',
    type: 'picklist',
    param: 'unitTypes',
    facetKey: 'unitType',
    accessor: (u) => u?.unitType ?? u?.unit_type,
  },
  {
    id: 'unitDevelopmentType',
    label: 'Development Type',
    type: 'picklist',
    param: 'unitDevelopmentTypes',
    facetKey: 'unitDevelopmentType',
    accessor: (u) => u?.unitDevelopmentType ?? u?.unit_development_type,
  },
  {
    id: 'state',
    label: 'State',
    type: 'picklist',
    param: 'states',
    facetKey: 'state',
    accessor: (u) => u?.state,
  },
  {
    id: 'unitSource',
    label: 'Unit Source',
    type: 'picklist',
    param: 'unitSources',
    facetKey: 'unitSource',
    accessor: (u) => u?.unitSource ?? u?.unit_source,
  },
  {
    id: 'estimateSource',
    label: 'Estimate Source',
    type: 'picklist',
    param: 'estimateSources',
    facetKey: 'estimateSource',
    accessor: (u) => u?.estimateSource ?? u?.estimate_source,
  },
  {
    id: 'basinTier',
    label: 'Basin Tier',
    type: 'picklist',
    param: 'basinTierIds',
    facetKey: 'basinTier',
    accessor: (u) => u?.basinTierId ?? u?.basin_tier_id,
  },
  {
    id: 'verificationStatus',
    label: 'Verification Status',
    type: 'picklist',
    param: 'verificationStatus',
    facetKey: 'verificationStatus',
    accessor: (u) => u?.verificationStatus ?? 'unverified',
  },
  // Numeric ranges
  {
    id: 'acreage',
    label: 'Acreage (ac)',
    type: 'numeric-range',
    param: 'acreage',
    facetKey: 'acreage',
    accessor: (u) => u?.acreage,
    minParam: 'min_acreage',
    maxParam: 'max_acreage',
  },
  {
    id: 'length',
    label: 'Length (ft)',
    type: 'numeric-range',
    param: 'length',
    facetKey: 'length',
    accessor: (u) => u?.length,
    minParam: 'min_length',
    maxParam: 'max_length',
  },
  {
    id: 'width',
    label: 'Width (ft)',
    type: 'numeric-range',
    param: 'width',
    facetKey: 'width',
    accessor: (u) => u?.width,
    minParam: 'min_width',
    maxParam: 'max_width',
  },
];

// Neutralises the params owned by `disabledIds` in a flat filter map. Shared by
// the wells and units "enabled filters" atoms — a disabled category keeps its
// definition in the UI but must not narrow any query.
//
// A picklist is set to `[]` rather than deleted: buildTcWellsQueryPayload seeds
// the header default (holeDirectionIds) and its applyUserFilters only drops it
// for a key that is PRESENT-but-empty. Deleting the key would leave the default
// in the payload. Same semantics as removing the filter card. Range params have
// no defaults, so deleting min/max is correct.
export function omitDisabledCategories<T extends Record<string, unknown>>(
  filters: T,
  disabledIds: string[],
  categories: FilterCategory[]
): T {
  if (disabledIds.length === 0) return filters;

  const next = { ...filters };
  for (const id of disabledIds) {
    const cat = categories.find((c) => c.id === id);
    if (!cat) continue;
    if (cat.type === 'picklist') {
      (next as Record<string, unknown>)[cat.param] = [];
    } else {
      delete next[cat.param];
    }
    if (cat.minParam) delete next[cat.minParam];
    if (cat.maxParam) delete next[cat.maxParam];
  }
  return next;
}

export const DEFAULT_WELL_QUALIFIER_RULE: ColorByConfig = {
  ruleId: 'default-well-qualifier',
  attribute: 'wellQualifier',
  selectedValues: ['Well Control', 'New Wells', 'Analog Wells'],
  valueColors: {
    'Well Control': '#C6C6C6',
    'New Wells': '#002D9C',
    'Analog Wells': '#4589FF',
  },
  applyTo: {
    map: true,
    probitPlot: false,
    monthlyVolumes: false,
    ratios: false,
    crossPlot: true,
  },
  enabled: true,
};

export const DEFAULT_VERIFICATION_STATUS_RULE_ID =
  'default-verification-status';

export const DEFAULT_VERIFICATION_STATUS_RULE: ColorByConfig = {
  ruleId: DEFAULT_VERIFICATION_STATUS_RULE_ID,
  attribute: 'verificationStatus',
  selectedValues: ['correct', 'incorrect'],
  valueColors: {
    correct: '#24a148',
    incorrect: '#da1e28',
  },
  applyTo: {
    map: true,
    probitPlot: false,
    monthlyVolumes: false,
    ratios: false,
    crossPlot: false,
  },
  enabled: true,
};
